import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import ReactDOM from 'react-dom';
import DraggableModal from 'chameleon/packages/blue-book-player/library/modal/DraggableModal';
import Modal from 'chameleon/packages/blue-book-player/library/modal';
import { isMobile } from 'chameleon/packages/blue-book-player/mobile/utils';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import { REFERENCE_SHEET_MODAL_WIDTHS } from 'chameleon/packages/blue-book-player/constants/modals';
import { MODAL_INNER_ROOT } from 'chameleon/packages/blue-book-player/constants/selectors';

const ReferenceSheetModal = forwardRef(
  ({ defaultPosition, defaultWidth, onClose, subject }, ref) => {
    const accessibilityResizeButton = (isExpanded) => {
      return {
        'aria-label': `${isExpanded ? 'reduce' : 'increase'} Reference Sheet size`,
      };
    };

    const accessibilityCloseButton = {
      'aria-label': 'Close Reference Sheet',
    };

    const basePath = 'external/reference-sheets';
    const sheetBaseUrl = `${FYM_FRONTEND_URL}/${basePath}/${subject}/index.html?v1`;
    const playerEl = useGetPlayerEl();

    const ModalContent = useMemo(
      () => (
        <div
          className="h-full flex relative"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          role="region"
          aria-label="Reference Sheet"
        >
          <iframe className="w-full h-full border-0" src={sheetBaseUrl} />
        </div>
      ),
      [sheetBaseUrl],
    );

    if (isMobile) {
      useImperativeHandle(ref, () => ({
        getPosition: () => defaultPosition,
        getWidth: () => defaultWidth,
      }));
      return ReactDOM.createPortal(
        <div className="fixed top-0 left-0 z-blueBook_referenceSheet">
          <Modal
            suppressEscapeKeyBehavior
            isCloseButtonInverted
            contentWithSpacing={false}
            isScrollableContent={false}
            title="Reference Sheet"
            topBarClassName="min-h-[42px] h-[42px] bg-bluebook-gray-900 text-bluebook-white1 pr-[14px] pl-[12px]"
            wrapperClassName="bg-white shadow-[0_0_50px_rgba(0,0,0,.2)] border border-bluebook-black"
            contentClassName="h-full"
            size="small"
            onClose={onClose}
            accessibilityCloseButton={accessibilityCloseButton}
            focuslockDisabled
          >
            {ModalContent}
          </Modal>
        </div>,
        playerEl?.querySelector(MODAL_INNER_ROOT),
      );
    }

    return (
      <DraggableModal
        title="Reference Sheet"
        defaultPosition={defaultPosition}
        defaultWidth={defaultWidth}
        onClose={onClose}
        contentWithSpacing={false}
        isScrollableContent={false}
        contentClassName="h-full"
        wrapperClassName="bg-white modal-max-height"
        draggableClassName="z-blueBook_referenceSheet"
        expandedWidth={REFERENCE_SHEET_MODAL_WIDTHS.expanded}
        collapsedWidth={REFERENCE_SHEET_MODAL_WIDTHS.collapsed}
        accessibilityDragButton={{
          'aria-label': 'Move Reference Sheet',
        }}
        accessibilityResizeButton={accessibilityResizeButton}
        accessibilityCloseButton={accessibilityCloseButton}
        ref={ref}
        focuslockDisabled
      >
        {ModalContent}
      </DraggableModal>
    );
  },
);

export default ReferenceSheetModal;
